import { Box, Divider } from '@mui/material';
import { Regions } from 'api/types/ReferenceData';
import { Bookmark } from 'components/common/Bookmark/Bookmark';
import { DynamicToolTip } from 'components/common/DynamicToolTip';
import SearchAward from 'components/common/Search/SearchCertificationList/SearchAwards';
import SearchCertifications from 'components/common/Search/SearchCertificationList/SearchCertificationList';
import { getFoodItemInfo, getItemInfo } from 'helpers/Search';
import { t } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import {
  IngredientIdentifier,
  SearchCategoryType,
  SearchDocument,
  SearchCategories,
  SearchTarget
} from 'types/Search';
import { BOOKMARK_SIZE } from '../types';
import { ProductType } from 'types/Products';
import { SegmentTag } from 'types/global';

const regionalAvailability: MRT_ColumnDef<SearchDocument> = {
  accessorKey: 'regional_availability',
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  header: 'Regional Avilability',
  accessorFn: row =>
    row.categories?.['regional-availability']?.regions
      ?.map(region => Regions.find(el => el.value === region.value)?.label)
      .join(', '),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
};
const getMultiValueTechnicalAttribute = (
  attribute:
    | SearchCategories.APPLICATIONS
    | SearchCategories.CHEMICAL_GROUP
    | SearchCategories.FUNCTIONS
    | SearchCategories.ORIGIN_SPECIES
): MRT_ColumnDef<SearchDocument> => ({
  accessorKey: attribute,
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  header: t(`searchTable.attributes.technical.${attribute}`),
  accessorFn: row =>
    row.categories?.technical?.[attribute]?.map(el => el.value).join(', '),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
});
const specializationField: MRT_ColumnDef<SearchDocument> = {
  accessorKey: 'specialization',
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  header: 'Specialization',
  accessorFn: row =>
    row.categories?.general?.specialization?.map(el => el.value).join(', '),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
};
const getSingleValueTechnicalAttribute = (
  attribute: 'usagelevel' | 'rci'
): MRT_ColumnDef<SearchDocument> => ({
  accessorKey: attribute,
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  header: t(`searchTable.attributes.technical.${attribute}`),
  accessorFn: row => row.categories?.technical?.[attribute]?.value,
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
});
export const getIngredientIdentifier = (
  identifier: IngredientIdentifier
): MRT_ColumnDef<SearchDocument> => ({
  accessorKey: identifier,
  header: t(`searchTable.attributes.identifiers.${identifier}`),
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  accessorFn: row => row?.identifiers?.[identifier],
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
});

const certificationField: MRT_ColumnDef<SearchDocument> = {
  accessorKey: 'certifications',
  header: 'Awards & Certifications',
  accessorFn: row => row?.categories?.technical?.certifications ?? '',
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true,
  Cell: ({ row }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {row.original.awards && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SearchAward
            awards={row.original.awards}
            certificationCount={
              row.original.categories?.technical?.certifications?.length
            }
          />
          {row.original.categories?.technical?.certifications && (
            <Divider orientation="vertical" flexItem />
          )}
        </Box>
      )}
      {row.original.categories?.technical?.certifications && (
        <SearchCertifications
          awardsCount={row.original.awards?.length}
          certifications={row.original.categories.technical.certifications}
        />
      )}
    </Box>
  )
};

const getClaimField = (
  claim: SearchCategoryType.PERFORMANCE | SearchCategoryType.SUSTAINABILITY
): MRT_ColumnDef<SearchDocument> => ({
  header: t(`searchTable.attributes.claims.${claim}`),
  accessorKey: claim,
  accessorFn: row =>
    row.categories?.technical?.claims
      ?.filter(el => el.properties?.type === claim)
      .map(el => el.value)
      .join(', '),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  ),
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true
});

const getPropertiesField = (
  property: SearchCategoryType.PHYSICAL | SearchCategoryType.CHEMICAL
): MRT_ColumnDef<SearchDocument> => ({
  header: t(`searchTable.attributes.properties.${property}`),
  accessorKey: property,
  accessorFn: row =>
    row.categories?.technical?.properties
      ?.filter(el => el.properties?.type === property)
      .map(el => el.value)
      .join(', '),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  ),
  enableHiding: true,
  enableColumnOrdering: true,
  visibleInShowHideMenu: true
});

//TODO: We have this only shown for support user, we may not need this any more
export const getLegacyAttributes = (
  isFood: boolean
): MRT_ColumnDef<SearchDocument> => ({
  accessorKey: 'attributes',
  header: isFood ? 'Food Attributes' : 'Attributes',
  enableColumnOrdering: false,
  accessorFn: row => (isFood ? getFoodItemInfo(row) : getItemInfo(row, false)),
  Cell: ({ renderedCellValue }) => (
    <DynamicToolTip text={renderedCellValue} lineClamp={2} />
  )
});

export const bookmarkField: MRT_ColumnDef<SearchDocument> = {
  accessorKey: 'bookmarked',
  enableHiding: false,
  enableColumnOrdering: false,
  visibleInShowHideMenu: false,
  accessorFn: row => row.bookmarked,
  Cell: ({ row }) => (
    <Bookmark target={row.original} isBookmarked={!!row.original.bookmarked} />
  ),
  header: '',
  size: BOOKMARK_SIZE
};

export const getDefaultFields = (
  target: SearchTarget,
  segment: SegmentTag
): MRT_ColumnDef<SearchDocument>[] => {
  switch (target) {
    case ProductType.INGREDIENTS:
      return [
        ...(segment === SegmentTag.PERSONALCARE
          ? [getIngredientIdentifier(IngredientIdentifier.INCI)]
          : []),
        getMultiValueTechnicalAttribute(SearchCategories.APPLICATIONS),
        getMultiValueTechnicalAttribute(SearchCategories.CHEMICAL_GROUP),
        getMultiValueTechnicalAttribute(SearchCategories.FUNCTIONS),
        getMultiValueTechnicalAttribute(SearchCategories.ORIGIN_SPECIES),
        getSingleValueTechnicalAttribute('rci'),
        getSingleValueTechnicalAttribute('usagelevel'),
        getClaimField(SearchCategoryType.PERFORMANCE),
        getClaimField(SearchCategoryType.SUSTAINABILITY),
        getPropertiesField(SearchCategoryType.PHYSICAL),
        getPropertiesField(SearchCategoryType.CHEMICAL),
        regionalAvailability,
        certificationField
      ];

    case ProductType.FORMULATIONS:
      return [
        getMultiValueTechnicalAttribute(SearchCategories.APPLICATIONS),
        getClaimField(SearchCategoryType.PERFORMANCE),
        getClaimField(SearchCategoryType.SUSTAINABILITY),
        getPropertiesField(SearchCategoryType.PHYSICAL),
        getPropertiesField(SearchCategoryType.CHEMICAL)
      ];
    case ProductType.SERVICES:
      return [
        getMultiValueTechnicalAttribute(SearchCategories.APPLICATIONS),
        specializationField
      ];
    default:
      return [];
  }
};
